import React, { useState } from "react";
import styles from "./styles.module.scss";
import DropdownButton from "app/components/DropdownButton";
import { useTranslation } from "react-i18next";
import ManageOfferingModal from "../../ManageOfferingModal";
import EditOfferingModal from "../../EditOfferingModal";
import OfferingActionModal from "../../OfferingActionModal";
import ActiveSubscriptionsModal from "../../OfferingActionModal/ActiveSubscriptionsModal";
import { RootState } from "typedefs";
import { useSelector } from "react-redux";
import Manage from "../../../../../public/images/product-manage.svg";
import Assign from "../../../../../public/images/product-assign.svg";
import Edit from "../../../../../public/images/product-edit.svg";
import Clone from "../../../../../public/images/product-clone.svg";
import Active from "../../../../../public/images/product-active.svg";
import Delete from "../../../../../public/images/product-delete.svg";
import { schedulingFetchPatch, schedulingFetchDelete } from "app/utils/request/scheduling/fetch";
import ToolTip from "app/routes/Scheduling/ToolTip";
import ManageOfferingModalSubscription from "../../ManageOfferingModalSubscription";
import AssignOfferingModal from "../../AssignOfferingModal";
import AssignOfferingModalSubscription from "../../AssignOfferingModalSubscription";
import { currency_symbol, offeringLimitNote, weekdayRestrictionLabel } from "../../NewOffering/utils";
import PackageDetailsModal from "../../PackageDetailsModal";
import ProductSessionsSummary from "../../AthleteOffering/ProductSessionSummary";
import { ProductTypeLabel } from "app/components/ProductTypeLabel";
import OnlinePayment from "../../../../../public/images/online-payment.svg";
import OfflinePayment from "../../../../../public/images/offline-payment.svg";
import { toast } from "react-toastify";
import { formatServerAmount } from "app/utils/currency";

export default function Offering({
  product,
  dataRefresher,
  enabledIntegration,
  facilityId,
  onClone,
}: {
  product: any;
  dataRefresher: () => void;
  enabledIntegration?: boolean;
  facilityId?: string;
  /** Opens the creation form seeded with this product, through the payment guard when it applies. */
  onClone: (product: any) => void;
}) {
  const { t } = useTranslation();
  const limitNote = offeringLimitNote(product, t);
  const [offeringManageModal, setOfferingManageModal] = useState(false);
  const [offeringAssignModal, setOfferingAssignModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const userId = useSelector((state: RootState) => state.user.self.id);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Only gray out online-only products when Stripe is disabled
  const requiresStripe = product.collectType === "online";
  const isProductActive = product.enabled && (enabledIntegration || !requiresStripe);
  const activateOrDeactivate = async () => {
    const productData: Record<string, unknown> = {
      active: !product.enabled,
      id: product.id,
    };

    // Only include coach_id when not in facility context
    if (!facilityId) {
      productData.coach_id = userId;
    }

    const endpoint = facilityId
      ? `/facilities/${facilityId}/stripe_products/${product.id}`
      : `/stripe_products/${product.id}`;

    schedulingFetchPatch(endpoint, productData)
      .then((data) => {
        if (!data || data.error) {
          throw new Error(data.error || "Error updating product status");
        }
        activateHandler();
        dataRefresher();
      })
      .catch((error) => {
        console.error("Error updating product status:", error);
      });
  };

  const offeringManageHandler = () => {
    setOfferingManageModal((prev) => !prev);
  };

  const offeringAssignHandler = () => {
    setOfferingAssignModal((prev) => !prev);
  };

  const editHandler = () => {
    setEditModal((prev) => !prev);
  };
  const cloneHandler = () => {
    onClone(product);
  };
  const activateHandler = () => {
    setActivateModal((prev) => !prev);
  };

  const deleteHandler = () => {
    setDeleteModal((prev) => !prev);
  };

  const deleteProduct = async () => {
    const endpoint = facilityId
      ? `/facilities/${facilityId}/stripe_products/${product.id}`
      : `/stripe_products/${product.id}`;

    try {
      await schedulingFetchDelete(endpoint);
      deleteHandler();
      dataRefresher();
    } catch (error) {
      deleteHandler();
      if (error.errors?.message && error.status === 422) {
        setErrorTitle(error.errors.title || t("Cannot Delete Offering"));
        setErrorMessage(error.errors.message);
      } else {
        toast.error(t("An unexpected error has occurred."));
      }
      console.error("Error deleting product:", error);
    }
  };

  const memberMenu = [
    {
      title: t("Manage"),
      onClick: () => {
        offeringManageHandler();
      },
      image: <img src={Manage} alt="manage" />,
    },
    {
      title: t("Assign"),
      onClick: () => {
        offeringAssignHandler();
      },
      image: <img src={Assign} alt="assign" />,
    },
    {
      title: t("Edit"),
      onClick: () => {
        editHandler();
      },
      image: <img src={Edit} alt="edit" />,
    },
    {
      title: t("Clone"),
      onClick: () => {
        cloneHandler();
      },
      image: <img src={Clone} alt="clone" />,
    },
    {
      title: product.enabled ? t("Deactivate") : t("Activate"),
      onClick: () => {
        activateHandler();
      },
      image: <img src={Active} alt="active" />,
    },
    {
      title: t("Delete"),
      onClick: () => {
        deleteHandler();
      },
      image: <img src={Delete} alt="delete" />,
    },
  ];

  const handleManage = () => {
    setDetailsModalOpen(false);
    setOfferingManageModal(true);
  };

  return (
    <>
      <div className={styles.product_card}>
        <div className={styles.product_header}>
          <ProductTypeLabel
            product={product}
            enabledIntegration={enabledIntegration}
          />
          <DropdownButton closeOnClick btnContent={<i className="bullets" />}>
            {memberMenu.map((item) => (
              <div key={item.title} className={styles.container}>
                <button onClick={() => item.onClick()}>
                  {item.image}
                  <p>{item.title}</p>
                </button>
              </div>
            ))}
          </DropdownButton>
        </div>
        <div className={styles.product_body}>
          <div className={styles.product_status}>
            <span
              className={
                isProductActive
                  ? styles.active_indicator
                  : styles.inactive_indicator
              }
            ></span>
            <div className={styles.titleRow}>
              <div className={styles.truncatedText}>
                {product.title?.length > 18
                  ? `${product?.title?.substring(0, 18)}...`
                  : product.title}
              </div>
              {product.title?.length > 18 && (
                <div className={styles.toolTipPosition}>
                  <ToolTip text={product.title} orientation="right" />
                </div>
              )}
            </div>
          </div>
          <ProductSessionsSummary
            product={product}
            onDetailsClick={() => setDetailsModalOpen(true)}
          />
        </div>
        <div className={styles.restriction_area}>
          {product.restrictWeekdays && !product.restrictWeekends && (
            <span className={styles.restriction_badge}>
              {weekdayRestrictionLabel(
                product.weekdayBeginAt ?? 0,
                product.weekdayEndAt ?? 1440,
                t("Weekdays only"),
              )}
            </span>
          )}
          {product.restrictWeekends && !product.restrictWeekdays && (
            <span className={styles.restriction_badge}>
              {t("Weekends & Holidays only")}
            </span>
          )}
        </div>
        {/* CFTB-3418: same treatment as the athlete storefront card — pinned
            directly above the footer's separator rather than flowing after the
            sessions summary, and emphasized so it doesn't read as description.
            product_body is a fixed height here too, so left in the flow the
            line moved around with the description. */}
        {limitNote && <p className={styles.config_note}>{limitNote}</p>}

        <div className={styles.product_footer}>
          <div className={styles.footer_row}>
            <div
              className={
                isProductActive
                  ? styles.product_price
                  : styles.product_price_inactive
              }
            >
              {currency_symbol[product.currency]}
              {formatServerAmount(product?.price)}
              {currency_symbol[product.currency] !== product.currency?.toUpperCase() && ` ${product.currency?.toUpperCase()}`}
            </div>
            <div className={styles.collect_icons}>
              {(product.collectType === "online" || product.collectType === "both") && (
                <div className={styles.iconWithTooltip}>
                  <img src={OnlinePayment} alt="Online Payment" className={styles.moneyIcon} />
                  <span className={styles.tooltipText}>{t("Collect Online")}</span>
                </div>
              )}
              {(product.collectType === "offline" || product.collectType === "both") && (
                <div className={styles.iconWithTooltip}>
                  <img src={OfflinePayment} alt="Offline Payment" className={styles.moneyIcon} />
                  <span className={styles.tooltipText}>{t("Collect Offline")}</span>
                </div>
              )}
            </div>
          </div>
          <p className={styles.payment_info}>
            {product?.productType === "product"
              ? t("One time payment")
              : product.recurring === "1"
                ? t("Billed monthly")
                : product.recurring === "12"
                  ? t("Billed annually")
                  : t("Billed every {{count}} months", {
                      count: product.recurring,
                    })}
          </p>
        </div>
      </div>
      {offeringManageModal &&
        (product.productType === "subscription" ? (
          <ManageOfferingModalSubscription
            product={product}
            isModalOpen={offeringManageModal}
            modalHandler={offeringManageHandler}
            stripeIntegrationEnabled={enabledIntegration}
            facilityId={facilityId}
          />
        ) : (
          <ManageOfferingModal
            product={product}
            isModalOpen={offeringManageModal}
            modalHandler={offeringManageHandler}
            stripeIntegrationEnabled={enabledIntegration}
            facilityId={facilityId}
          />
        ))}
      {offeringAssignModal &&
        (product.productType === "subscription" ? (
          <AssignOfferingModalSubscription
            product={product}
            isModalOpen={offeringAssignModal}
            modalHandler={offeringAssignHandler}
            stripeIntegrationEnabled={enabledIntegration}
            facilityId={facilityId}
          />
        ) : (
          <AssignOfferingModal
            product={product}
            isModalOpen={offeringAssignModal}
            modalHandler={offeringAssignHandler}
            stripeIntegrationEnabled={enabledIntegration}
            facilityId={facilityId}
          />
        ))}
      {detailsModalOpen && (
        <PackageDetailsModal
          product={product}
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          buttonTitle="Manage"
          onButtonClick={handleManage}
        />
      )}

      {editModal && (
        <EditOfferingModal
          product={product}
          isModalOpen={editModal}
          modalHandler={editHandler}
          dataRefresher={dataRefresher}
        />
      )}
      {activateModal && (
        <OfferingActionModal
          actions={{
            onCancel: () => activateHandler(),
            onConfirm: () => activateOrDeactivate(),
          }}
          data={{
            operation: product.enabled ? "delete" : "edit",
            title: product.enabled
              ? `${t("Are you sure you want to deactivate")} "${product.title}"?`
              : `${t("Want to activate")} "${product.title}"?`,
            content: product.enabled
              ? product?.productType === "product"
                ? t(
                    "Deactivate removes the ability of Athletes to Purchase this offering.",
                  )
                : t(
                    "Deactivate removes the ability of Athletes to Purchase this offering.  Auto renewals will not be turned off. You can cancel active subscriptions manually if needed.",
                  )
              : t("This offering will become purchasable by Athletes."),
            button_text: product.enabled
              ? t("Yes, deactivate it")
              : t("Yes, activate it"),
          }}
        />
      )}
      {deleteModal && (
        <OfferingActionModal
          actions={{
            onCancel: () => deleteHandler(),
            onConfirm: () => deleteProduct(),
          }}
          data={{
            operation: "delete",
            title: `${t("Are you sure you want to delete")} "${product.title}"?`,
            content: t("This action cannot be undone. The offering will be permanently removed."),
            button_text: t("Yes, delete it"),
          }}
        />
      )}
      {errorMessage && (
        <ActiveSubscriptionsModal
          message={errorMessage}
          onClose={() => setErrorMessage("")}
          title={errorTitle}
        />
      )}
    </>
  );
}
